import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import {
  DashboardIcon, CampaignIcon, ProposalIcon, PayoutsIcon,
  HuntersIcon, ProfileIcon, LeaderboardIcon,
  UsersIcon, RolesIcon, TiersIcon, AnnouncementsIcon,
  PaymentIcon, SettingsIcon, SignOutIcon,
  ChevronLeftIcon, ChevronRightIcon,
} from './Icons';

const mainMenu = [
  { path: '/', label: 'Dashboard', Icon: DashboardIcon, hideForRoles: ['basic', 'admin_operator'] },
  { path: '/campaigns', label: 'Campaigns', Icon: CampaignIcon },
  { path: '/proposals', label: 'Proposals', Icon: ProposalIcon, hideForRoles: ['basic', 'hunter', 'admin_operator'] },
  { path: '/payouts', label: 'Payouts', Icon: PayoutsIcon, isPayouts: true },
  { path: '/leaderboard', label: 'Leaderboard', Icon: LeaderboardIcon, showForRoles: ['hunter', 'basic'] },
  { path: '/hunters', label: 'Hunters', Icon: HuntersIcon, hideForRoles: ['basic', 'hunter', 'admin_operator'] },
  { path: '/profile', label: 'Profile', Icon: ProfileIcon },
];

const adminMenu = [
  { path: '/users', label: 'Users', Icon: UsersIcon },
  { path: '/roles', label: 'Roles', Icon: RolesIcon },
  { path: '/tiers', label: 'Tiers', Icon: TiersIcon },
  { path: '/announcements', label: 'Announcements', Icon: AnnouncementsIcon },
  { path: '/testing-payment', label: 'Testing Payment', Icon: PaymentIcon },
  { path: '/settings', label: 'Settings', Icon: SettingsIcon },
];

const Sidebar = ({ collapsed, onToggle }) => {
  const { profile, signOut } = useAuth();
  const location = useLocation();

  const isActive = (path) => location.pathname === path;

  const isVisible = (item) => {
    if (item.hideForRoles?.includes(profile?.role)) return false;
    if (item.showForRoles && !item.showForRoles.includes(profile?.role)) return false;
    return true;
  };

  const getLabel = (item) => {
    if (item.isPayouts && (profile?.role === 'hunter' || profile?.role === 'basic')) {
      return 'Content Tracking';
    }
    return item.label;
  };

  const shouldShow = (item) => {
    if (item.isPayouts) {
      if (profile?.role === 'campaign_manager' || profile?.role === 'admin_operator') return false;
    }
    return isVisible(item);
  };

  const NavItem = ({ item }) => {
    if (!shouldShow(item)) return null;
    const active = isActive(item.path);
    return (
      <Link
        to={item.path}
        title={collapsed ? getLabel(item) : undefined}
        className={`flex items-center gap-3 px-3 py-[10px] rounded-lg transition-colors duration-150 ${
          collapsed ? 'justify-center' : ''
        } ${
          active
            ? 'bg-[#ecf8fb] text-[#2a798c]'
            : 'text-[#3b3b3b] hover:bg-gray-50 hover:text-[#2a798c]'
        }`}
      >
        <item.Icon />
        {!collapsed && (
          <span className="text-[14px] font-normal leading-5 tracking-[-0.06px] truncate">
            {getLabel(item)}
          </span>
        )}
      </Link>
    );
  };

  return (
    <div
      className={`${collapsed ? 'w-[80px]' : 'w-[244px]'} h-screen flex-shrink-0 p-4 transition-[width] duration-300`}
    >
      <div
        className="bg-white rounded-3xl h-full flex flex-col py-5 px-3 overflow-hidden"
        style={{ boxShadow: '0px 2px 8px 0px rgba(0,0,0,0.04)' }}
      >
        {/* Logo + Toggle */}
        <div className={`flex items-center mb-6 px-1 min-h-[38px] ${collapsed ? 'justify-center' : 'justify-between'}`}>
          {!collapsed && (
            <div className="flex items-center gap-1">
              <span
                className="font-extrabold text-[20px] leading-none tracking-tight"
                style={{ color: '#3fb5d2', fontFamily: 'Inter, sans-serif' }}
              >
                SINII
              </span>
              <svg width="12" height="12" viewBox="0 0 24 24" fill="#3fb5d2" className="flex-shrink-0">
                <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 16.77l-6.18 3.25L7 13.14 2 9.27l6.91-1.01z"/>
              </svg>
            </div>
          )}
          <button
            onClick={onToggle}
            className="text-[#8a8a8a] hover:text-[#3b3b3b] transition-colors p-1.5 rounded-lg hover:bg-gray-50"
          >
            {collapsed ? <ChevronRightIcon /> : <ChevronLeftIcon />}
          </button>
        </div>

        {/* Main Nav */}
        <nav className="flex-1 flex flex-col gap-0.5 overflow-y-auto overflow-x-hidden">
          {mainMenu.map((item) => (
            <NavItem key={item.path + item.label} item={item} />
          ))}

          {/* Admin Section */}
          {profile?.role === 'admin' && (
            <>
              <div className={`mt-3 mb-1 pt-3 border-t border-[#e5e5e5] ${collapsed ? 'mx-1' : 'px-3'}`}>
                {!collapsed && (
                  <span className="text-[#8a8a8a] text-[12px] uppercase tracking-wider font-medium">
                    Admin
                  </span>
                )}
              </div>
              {adminMenu.map((item) => (
                <NavItem key={item.path} item={item} />
              ))}
            </>
          )}
        </nav>

        {/* Logout */}
        <div className="pt-3 border-t border-[#e5e5e5]">
          <button
            onClick={signOut}
            title={collapsed ? 'Log Out' : undefined}
            className={`flex items-center gap-3 w-full px-3 py-[10px] rounded-lg text-[#c62828] hover:bg-red-50 transition-colors ${
              collapsed ? 'justify-center' : ''
            }`}
          >
            <SignOutIcon />
            {!collapsed && (
              <span className="text-[14px] font-normal leading-5 tracking-[-0.06px]">Log Out</span>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default Sidebar;
